import React, { useEffect, useMemo, useState } from "react";

type AccessType = "FREE" | "PENDING" | "ACTIVE";

type ActiveSection =
  | "overview"
  | "courses"
  | "live"
  | "tests"
  | "notifications"
  | "settings";

type StudentInfo = {
  id: number | null;
  fullName: string;
  email: string;
  phone: string;
  studentClass: string;
  course: string;
  profileImage: string;
};

type Course = {
  id: number;
  title: string;
  category?: string;
  teacher?: string;
  price?: number;
  image_url?: string;
  status?: string;
  progress?: number;
  desc?: string;
  short_desc?: string;
  duration?: string;
  level?: string;
  tag?: string;
  seoTitle?: string;
  features?: string[];
  slug?: string;
};

type LiveStatus = "Live" | "Scheduled" | "Completed" | "Cancelled";

type LiveClass = {
  id: number;
  title: string;
  course: string;
  batch: string;
  teacher: string;
  subject: string;
  platform: string;
  meetingLink: string;
  roomName?: string;
  date: string;
  startTime: string;
  duration: number;
  status: LiveStatus;
  recordingUrl?: string;
  materials?: string;
};

type PendingEnrollment = {
  id?: number;
  courseTitle?: string;
  batchName?: string;
  planName?: string;
  amount?: number;
  status?: string;
  paymentStatus?: string;
};

type NotificationItem = {
  id: number;
  title: string;
  desc: string;
  time: string;
  type: "live" | "test" | "payment" | "note";
};

type DemoSubject = {
  title: string;
  desc: string;
  tag: string;
  icon: string;
  youtubeUrl: string;
};

// const PROFILE_API = "http://localhost:9090/api/profile";
// const COURSES_API = "http://localhost:9090/api/courses";
// const LIVE_CLASS_API = "http://localhost:9091/api/student/live-classes";
// const ENROLLMENTS_API = "http://localhost:9091/api/enrollments/by-email";
// const TESTS_API = "http://localhost:9090/api/tests";


const PROFILE_API = "https://node-api.mediitacademy.com/api/profile";
const COURSES_API = "https://node-api.mediitacademy.com/api/courses";
const LIVE_CLASS_API = "https://api.mediitacademy.com/api/student/live-classes";
const ENROLLMENTS_API = "https://api.mediitacademy.com/api/enrollments/by-email";
const TESTS_API = "https://node-api.mediitacademy.com/api/tests";

const HERO_IMAGE_URL =
  "https://static.vecteezy.com/system/resources/previews/050/817/647/non_2x/cheerful-young-woman-student-freelancer-using-digital-tablet-standing-side-view-isolate-on-transparent-background-png.png";

const notifications: NotificationItem[] = [
  {
    id: 1,
    title: "Live class reminder",
    desc: "Next live class schedule hone par yaha alert milega.",
    time: "Today",
    type: "live",
  },
  {
    id: 2,
    title: "Demo access available",
    desc: "Aap free demo classes aur course preview dekh sakte ho.",
    time: "Now",
    type: "note",
  },
  {
    id: 3,
    title: "Test practice",
    desc: "Subject-wise demo quiz aur test series preview available hai.",
    time: "2h ago",
    type: "test",
  },
];

const defaultDemoSubjects: DemoSubject[] = [
  {
    title: "Mathematics Demo",
    desc: "Basic concept clarity, formulas aur practice questions.",
    tag: "Class Demo",
    icon: "∑",
    youtubeUrl:
      "https://www.youtube.com/results?search_query=class+maths+foundation+demo+lecture",
  },
  {
    title: "Science Demo",
    desc: "NCERT-based science concept explanation with examples.",
    tag: "School Foundation",
    icon: "⚗",
    youtubeUrl:
      "https://www.youtube.com/results?search_query=class+science+foundation+demo+lecture",
  },
  {
    title: "Physics Demo",
    desc: "Numericals, laws and exam-oriented concept building.",
    tag: "JEE / NEET",
    icon: "Φ",
    youtubeUrl:
      "https://www.youtube.com/results?search_query=physics+jee+neet+demo+lecture",
  },
  {
    title: "Biology Demo",
    desc: "NCERT line-by-line Biology explanation for NEET basics.",
    tag: "NEET",
    icon: "DNA",
    youtubeUrl:
      "https://www.youtube.com/results?search_query=neet+biology+ncert+demo+lecture",
  },
  {
    title: "Test Series Demo",
    desc: "Mock test pattern, score analysis aur practice preview.",
    tag: "Test Series",
    icon: "✓",
    youtubeUrl:
      "https://www.youtube.com/results?search_query=jee+neet+test+series+demo",
  },
];

const Dashboard: React.FC = () => {
  const [activeSection, setActiveSection] = useState<ActiveSection>("overview");
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  const [student, setStudent] = useState<StudentInfo>({
    id: null,
    fullName: "",
    email: "",
    phone: "",
    studentClass: "",
    course: "",
    profileImage: "",
  });

  const [courses, setCourses] = useState<Course[]>([]);
  const [liveClasses, setLiveClasses] = useState<LiveClass[]>([]);
  const [myEnrollments, setMyEnrollments] = useState<any[]>([]);
  const [tests, setTests] = useState<any[]>([]);

  const [loading, setLoading] = useState(true);

  const getToken = () => localStorage.getItem("token") || "";

  const getStoredStudentId = () => {
    const directId = localStorage.getItem("studentId") || localStorage.getItem("userId") || localStorage.getItem("id");
    if (directId) return Number(directId);
    try {
      const user = JSON.parse(localStorage.getItem("user") || "{}");
      return Number(user?.id || user?.studentId || 1);
    } catch {
      return 1;
    }
  };

  const normalizeStatus = (value: any): LiveStatus => {
    const status = String(value || "Scheduled").toLowerCase();
    if (status === "live") return "Live";
    if (status === "completed") return "Completed";
    if (status === "cancelled" || status === "canceled") return "Cancelled";
    return "Scheduled";
  };

  const normalizeTime = (value: any) => {
    if (!value) return "";
    return String(value).slice(0, 5);
  };

  const normalizeLiveClass = (item: any): LiveClass => {
    return {
      id: Number(item.id),
      title: item.title || "",
      course: item.course || "",
      batch: item.batch || "",
      teacher: item.teacher || "",
      subject: item.subject || "",
      platform: item.platform || "Website Live",
      meetingLink: item.meetingLink || item.meeting_link || "",
      roomName: item.roomName || item.room_name || "",
      date: item.date || item.classDate || item.class_date || "",
      startTime: normalizeTime(item.startTime || item.start_time),
      duration: Number(item.duration || 60),
      status: normalizeStatus(item.status),
      recordingUrl: item.recordingUrl || item.recording_url || "",
      materials: item.materials || "",
    };
  };

  const fetchProfile = async () => {
    const token = getToken();
    if (!token) {
      window.location.href = "/login";
      return null;
    }

    const response = await fetch(PROFILE_API, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data?.error || "Profile load nahi ho paaya.");
    }

    const nextStudent: StudentInfo = {
      id: Number(data.id || data.user_id || getStoredStudentId()),
      fullName: data.full_name || data.name || "Student",
      email: data.email || "",
      phone: data.mobile_number || data.phone || "",
      studentClass: data.school || data.studentClass || data.class || "",
      course: data.course || "",
      profileImage: data.profile_image
        ? `https://node-api.mediitacademy.com${data.profile_image}`
        : "",
    };

    setStudent(nextStudent);
    localStorage.setItem("studentId", String(nextStudent.id || 1));
    localStorage.setItem("studentName", nextStudent.fullName || "Student");

    if (nextStudent.email) {
      localStorage.setItem("email", nextStudent.email);
    }

    return nextStudent;
  };

  const fetchCourses = async () => {
    const response = await fetch(COURSES_API);
    if (!response.ok) {
      throw new Error("Courses load nahi ho paaye.");
    }
    const data = await response.json();
    setCourses(Array.isArray(data) ? data : []);
  };

  const fetchLiveClasses = async (studentId: number) => {
    try {
      const response = await fetch(`${LIVE_CLASS_API}/my?studentId=${studentId}`);
      if (!response.ok) {
        throw new Error("Student live classes API failed");
      }
      const data = await response.json();
      setLiveClasses(Array.isArray(data) ? data.map(normalizeLiveClass) : []);
    } catch {
      try {
        const fallback = await fetch(LIVE_CLASS_API);
        if (fallback.ok) {
          const data = await fallback.json();
          setLiveClasses(Array.isArray(data) ? data.map(normalizeLiveClass) : []);
        }
      } catch {
        setLiveClasses([]);
      }
    }
  };

  const fetchMyEnrollments = async (email: string) => {
    if (!email) return;
    try {
      const response = await fetch(`${ENROLLMENTS_API}/${encodeURIComponent(email)}`);
      if (!response.ok) return;
      const data = await response.json();
      setMyEnrollments(Array.isArray(data) ? data : [data]);
    } catch {
      setMyEnrollments([]);
    }
  };

  const fetchTests = async () => {
    try {
      const response = await fetch(TESTS_API);
      if (response.ok) {
        const data = await response.json();
        setTests(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.error("Tests fetch failed", err);
    }
  };

  const loadDashboard = async () => {
    setLoading(true);
    try {
      const profile = await fetchProfile();

      if (profile) {
        await Promise.allSettled([
          fetchCourses(),
          fetchLiveClasses(Number(profile.id || getStoredStudentId())),
          fetchMyEnrollments(profile.email || ""),
          fetchTests()
        ]);
      }
    } catch (error) {
      console.error("Student dashboard error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDashboard();
  }, []);

  const activeEnrollment = useMemo(() => {
    return myEnrollments.find((e) => {
      const status = String(e.status || e.paymentStatus || "").toUpperCase().trim();
      return e.courseAccessEnabled || e.accessEnabled || ["APPROVED", "PAID", "SUCCESS"].includes(status);
    });
  }, [myEnrollments]);

  const pendingEnrollment = useMemo(() => {
    return myEnrollments.find((e) => {
      const status = String(e.status || e.paymentStatus || "").toUpperCase().trim();
      return ["PENDING", "VERIFY", "SUBMITTED"].includes(status) && !e.courseAccessEnabled && !e.accessEnabled;
    });
  }, [myEnrollments]);

  const activeCourse = useMemo(() => {
    if (activeEnrollment) {
      return courses.find(c => c.id === activeEnrollment.courseId || c.id === activeEnrollment.course_id) || null;
    }
    if (student.course) {
      return courses.find(
        (course) =>
          String(course.title || "").toLowerCase().trim() ===
          String(student.course || "").toLowerCase().trim()
      ) || null;
    }
    return null;
  }, [activeEnrollment, courses, student.course]);

  const accessType: AccessType = useMemo(() => {
    if (activeEnrollment) return "ACTIVE";
    if (pendingEnrollment) return "PENDING";
    return "FREE";
  }, [activeEnrollment, pendingEnrollment]);

  const liveNowClasses = liveClasses.filter((item) => item.status === "Live");
  const upcomingClasses = liveClasses.filter((item) => item.status === "Scheduled");
  const completedClasses = liveClasses.filter((item) => item.status === "Completed");

  const recommendedCourses = courses
    .filter((course) => course.title !== student.course)
    .slice(0, 4);

  const demoSubjects = useMemo(() => {
    const classText = student.studentClass.toLowerCase();
    const courseText = student.course.toLowerCase();

    if (courseText.includes("neet")) {
      return defaultDemoSubjects.filter((item) =>
        ["Biology Demo", "Physics Demo", "Test Series Demo"].includes(item.title)
      );
    }

    if (courseText.includes("jee")) {
      return defaultDemoSubjects.filter((item) =>
        ["Mathematics Demo", "Physics Demo", "Test Series Demo"].includes(item.title)
      );
    }

    if (
      classText.includes("7") ||
      classText.includes("8") ||
      classText.includes("9") ||
      classText.includes("10")
    ) {
      return defaultDemoSubjects.filter((item) =>
        ["Mathematics Demo", "Science Demo", "Test Series Demo"].includes(item.title)
      );
    }

    return defaultDemoSubjects.slice(0, 4);
  }, [student.studentClass, student.course]);

  const profileInitial = student.fullName ? student.fullName.charAt(0).toUpperCase() : "S";

  const joinLiveClass = async (liveClass: LiveClass) => {
    if (accessType !== "ACTIVE") {
      setActiveSection("courses");
      return;
    }

    const studentId = student.id || getStoredStudentId();
    const studentName = student.fullName || "Student";

    try {
      await fetch(
        `${LIVE_CLASS_API}/${liveClass.id}/join?studentId=${studentId}&studentName=${encodeURIComponent(studentName)}`,
        { method: "POST" }
      );
    } catch {
      // room open kar dena hai
    }

    window.location.href = `/student-dashboard/live-class-room/${liveClass.id}`;
  };

  const logoutUser = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("student");
    localStorage.removeItem("user");
    localStorage.removeItem("studentId");
    localStorage.removeItem("userId");
    localStorage.removeItem("id");
    localStorage.removeItem("studentName");
    localStorage.removeItem("name");
    localStorage.removeItem("email");
    localStorage.removeItem("mobile");
    localStorage.removeItem("phone");

    window.location.href = "/login";
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="min-h-screen bg-[#edf4ff] text-[#101827] font-['Inter']">
      <style>{`
        ::selection {
          background: #123b7a;
          color: #ffffff;
        }
      `}</style>
      <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/95 px-4 py-3 shadow-sm backdrop-blur-xl sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-[1680px] items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#1d4ed8] text-2xl font-black text-white shadow-sm">
              M
            </div>

            <div className="min-w-0">
              <p className="text-[11px] font-black uppercase tracking-[0.22em] text-blue-700">
                Student Dashboard
              </p>
              <h2 className="truncate text-xl font-black text-slate-950 sm:text-2xl">
                Hello, {student.fullName || "Student"} 👋
              </h2>
            </div>
          </div>

          <div className="hidden min-w-[320px] max-w-2xl flex-1 xl:block">
            <div className="flex h-12 items-center gap-3 rounded-[18px] border border-slate-200 bg-slate-50 px-4 transition focus-within:border-blue-300 focus-within:bg-white focus-within:shadow-sm">
              <span className="text-lg text-slate-400">⌕</span>
              <input
                type="text"
                placeholder="Search courses, tests, classes..."
                className="h-full w-full bg-transparent text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400"
              />
            </div>
          </div>

          <div className="hidden items-center gap-2 lg:flex">
            <TopNavButton
              label="Dashboard"
              active={activeSection === "overview"}
              onClick={() => setActiveSection("overview")}
            />
            <TopNavButton
              label="Courses"
              active={activeSection === "courses"}
              onClick={() => setActiveSection("courses")}
            />
            <TopNavButton
              label="Live"
              active={activeSection === "live"}
              onClick={() => setActiveSection("live")}
            />
            <TopNavButton
              label="Tests"
              active={activeSection === "tests"}
              onClick={() => setActiveSection("tests")}
            />
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={() => setActiveSection("notifications")}
              className="relative flex h-11 w-11 items-center justify-center rounded-2xl border border-slate-200 bg-white text-lg shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:bg-blue-50"
              title="Notifications"
            >
              🔔
              <span className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-[10px] font-black text-white ring-2 ring-white">
                {notifications.length}
              </span>
            </button>

            <StudentTopDropdown
              student={student}
              profileInitial={profileInitial}
              accessType={accessType}
              activeSection={activeSection}
              open={profileDropdownOpen}
              setOpen={setProfileDropdownOpen}
              setActiveSection={setActiveSection}
              logoutUser={logoutUser}
            />
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1680px] p-4 sm:p-6 lg:p-8">
        {activeSection === "overview" && (
          <OverviewSection
            student={student}
            accessType={accessType}
            liveNowClasses={liveNowClasses}
            upcomingClasses={upcomingClasses}
            completedClasses={completedClasses}
            activeCourse={activeCourse}
            demoSubjects={demoSubjects}
            setActiveSection={setActiveSection}
            joinLiveClass={joinLiveClass}
            activeEnrollment={activeEnrollment}
          />
        )}

        {activeSection === "courses" && (
          <CoursesSection
            courses={courses}
            student={student}
            accessType={accessType}
            recommendedCourses={recommendedCourses}
            demoSubjects={demoSubjects}
            myEnrollments={myEnrollments}
          />
        )}

        {activeSection === "live" && (
          <LiveClassesSection
            accessType={accessType}
            liveNowClasses={liveNowClasses}
            upcomingClasses={upcomingClasses}
            completedClasses={completedClasses}
            demoSubjects={demoSubjects}
            joinLiveClass={joinLiveClass}
            setActiveSection={setActiveSection}
            activeEnrollment={activeEnrollment}
            activeCourse={activeCourse}
          />
        )}

        {activeSection === "tests" && (
          <TestsSection
            accessType={accessType}
            demoSubjects={demoSubjects}
            setActiveSection={setActiveSection}
            tests={tests}
            activeEnrollment={activeEnrollment}
            activeCourse={activeCourse}
          />
        )}

        {activeSection === "notifications" && (
          <NotificationsSection notifications={notifications} />
        )}

        {activeSection === "settings" && (
          <SettingsSection logoutUser={logoutUser} />
        )}
      </main>
    </div>
  );
};

const LoadingScreen = () => {
  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-white">
      <style>{`
        @keyframes yellowBarUpDown {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-16px); }
        }
        @keyframes yellowCircleStep {
          0%, 100% { transform: translate(0, 0); }
          25% { transform: translate(32px, -16px); }
          50% { transform: translate(64px, 0); }
          75% { transform: translate(96px, -16px); }
        }
        @keyframes yellowDotPulse {
          0%, 100% { transform: translateY(0); opacity: .45; }
          50% { transform: translateY(-8px); opacity: 1; }
        }
      `}</style>

      <div className="relative flex flex-col items-center">
        <div className="relative flex h-40 w-44 items-end justify-center rounded-[34px] border border-slate-100 bg-white p-7">
          <div
            className="absolute left-7 top-8 h-5 w-5 rounded-full border-[5px] border-yellow-500 bg-yellow-400"
            style={{ animation: "yellowCircleStep 1.7s ease-in-out infinite" }}
          />

          <div className="flex h-full items-end gap-3">
            {[48, 78, 106, 134].map((height, index) => (
              <span
                key={height}
                className="block w-5 rounded-t-xl border-[5px] border-yellow-500 bg-yellow-400"
                style={{
                  height,
                  animation: "yellowBarUpDown 1.25s ease-in-out infinite",
                  animationDelay: `${index * 140}ms`,
                }}
              />
            ))}
          </div>
        </div>

        <div className="mt-7 flex items-center gap-2">
          {[0, 1, 2].map((item) => (
            <span
              key={item}
              className="h-3 w-3 rounded-full bg-yellow-400"
              style={{
                animation: "yellowDotPulse 1s ease-in-out infinite",
                animationDelay: `${item * 170}ms`,
              }}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const StudentTopDropdown = ({
  student,
  profileInitial,
  accessType,
  activeSection,
  open,
  setOpen,
  setActiveSection,
  logoutUser,
}: {
  student: StudentInfo;
  profileInitial: string;
  accessType: AccessType;
  activeSection: ActiveSection;
  open: boolean;
  setOpen: (value: boolean) => void;
  setActiveSection: (value: ActiveSection) => void;
  logoutUser: () => void;
}) => {
  const menuItems: {
    label: string;
    desc: string;
    icon: string;
    section?: ActiveSection;
    href?: string;
    danger?: boolean;
  }[] = [
    {
      label: "My Course",
      desc: student.course || "Demo course preview",
      icon: "📘",
      section: "courses",
    },
    {
      label: "Live Classes",
      desc: "Schedule aur classroom",
      icon: "🎥",
      section: "live",
    },
    {
      label: "Test Series",
      desc: "Mock test aur practice",
      icon: "🧪",
      section: "tests",
    },
    {
      label: "Notifications",
      desc: "Class aur course alerts",
      icon: "🔔",
      section: "notifications",
    },
    {
      label: "Settings",
      desc: "Account preferences",
      icon: "⚙️",
      section: "settings",
    },
    {
      label: "Full Profile",
      desc: "Photo aur details update",
      icon: "👤",
      href: "/student-dashboard/profile",
    },
    {
      label: "Logout",
      desc: "Session close karein",
      icon: "↪",
      danger: true,
    },
  ];

  const handleItemClick = (item: (typeof menuItems)[number]) => {
    setOpen(false);

    if (item.danger) {
      logoutUser();
      return;
    }

    if (item.href) {
      window.location.href = item.href;
      return;
    }

    if (item.section) {
      setActiveSection(item.section);
    }
  };

  return (
    <div className="relative">
      {open && (
        <button
          className="fixed inset-0 z-30 cursor-default bg-transparent"
          onClick={() => setOpen(false)}
          aria-label="Close student menu"
        />
      )}

      <button
        onClick={() => setOpen(!open)}
        className="relative z-40 flex h-12 items-center gap-3 rounded-[18px] border border-slate-200 bg-white px-2.5 pr-3 shadow-sm transition hover:-translate-y-0.5 hover:border-blue-200 hover:shadow-md sm:min-w-[210px]"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-blue-50 text-sm font-black text-blue-700 ring-1 ring-slate-100">
          {student.profileImage ? (
            <img
              src={student.profileImage}
              alt={student.fullName || "Student"}
              className="h-full w-full object-cover"
            />
          ) : (
            profileInitial
          )}
        </span>

        <span className="hidden min-w-0 text-left sm:block">
          <span className="block truncate text-sm font-black leading-4 text-slate-950">
            {student.fullName || "Student"}
          </span>
          <span className="mt-0.5 block truncate text-[11px] font-bold text-slate-500">
            {student.studentClass || "Student"} •{" "}
            {accessType === "ACTIVE" ? "Premium" : "Demo"}
          </span>
        </span>

        <span
          className={`ml-auto hidden text-sm font-black text-slate-400 transition sm:block ${
            open ? "rotate-180" : ""
          }`}
        >
          ⌄
        </span>
      </button>

      {open && (
        <div className="absolute right-0 top-[58px] z-40 w-[315px] overflow-hidden rounded-[30px] border border-slate-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.18)]">
          <div className="bg-gradient-to-br from-[#061226] via-[#08234a] to-[#123b7a] p-4 text-white">
            <div className="flex items-center gap-3">
              <div className="flex h-14 w-14 items-center justify-center overflow-hidden rounded-2xl bg-white text-xl font-black text-blue-700 shadow-lg">
                {student.profileImage ? (
                  <img
                    src={student.profileImage}
                    alt={student.fullName || "Student"}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  profileInitial
                )}
              </div>

              <div className="min-w-0">
                <h3 className="truncate text-base font-black">
                  {student.fullName || "Student"}
                </h3>
                <p className="mt-1 truncate text-xs font-bold text-blue-100">
                  {student.email || student.phone || "Student account"}
                </p>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-white/10 bg-white/10 px-4 py-3 backdrop-blur">
              <p className="text-[11px] font-black uppercase tracking-wide text-blue-100">
                Current Access
              </p>
              <p className="mt-1 text-sm font-black">
                {accessType === "ACTIVE"
                  ? student.course || "Premium Active"
                  : accessType === "PENDING"
                  ? "Payment Pending"
                  : "Demo Access Active"}
              </p>
            </div>
          </div>

          <div className="p-3">
            {menuItems.map((item) => {
              const isActive = item.section && activeSection === item.section;

              return (
                <button
                  key={item.label}
                  onClick={() => handleItemClick(item)}
                  className={`group flex w-full items-center gap-3 rounded-[20px] px-3 py-3 text-left transition ${
                    item.danger
                      ? "hover:bg-red-50"
                      : isActive
                      ? "bg-blue-50"
                      : "hover:bg-slate-50"
                  }`}
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl text-lg transition group-hover:scale-105 ${
                      item.danger
                        ? "bg-red-50 text-red-600"
                        : isActive
                        ? "bg-blue-600 text-white"
                        : "bg-slate-100 text-slate-700"
                    }`}
                  >
                    {item.icon}
                  </span>

                  <span className="min-w-0 flex-1">
                    <span
                      className={`block text-sm font-black ${
                        item.danger ? "text-red-600" : "text-slate-950"
                      }`}
                    >
                      {item.label}
                    </span>
                    <span className="mt-0.5 block truncate text-xs font-semibold text-slate-500">
                      {item.desc}
                    </span>
                  </span>

                  <span className="text-slate-300 transition group-hover:translate-x-1">
                    ›
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const TopNavButton = ({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      onClick={onClick}
      className={`rounded-2xl px-4 py-2 text-sm font-black transition ${
        active
          ? "bg-blue-600 text-white shadow-sm"
          : "bg-slate-50 text-slate-600 hover:bg-blue-50 hover:text-blue-700"
      }`}
    >
      {label}
    </button>
  );
};

const OverviewSection = ({
  student,
  accessType,
  liveNowClasses,
  upcomingClasses,
  completedClasses,
  activeCourse,
  demoSubjects,
  setActiveSection,
  joinLiveClass,
  activeEnrollment,
}: {
  student: StudentInfo;
  accessType: AccessType;
  liveNowClasses: LiveClass[];
  upcomingClasses: LiveClass[];
  completedClasses: LiveClass[];
  activeCourse: Course | null;
  demoSubjects: DemoSubject[];
  setActiveSection: (value: ActiveSection) => void;
  joinLiveClass: (value: LiveClass) => void;
  activeEnrollment: any;
}) => {
  const enrolledBatchName = activeEnrollment?.batchName || activeEnrollment?.batch_name;
  const enrolledCourseTitle = activeCourse?.title;

  const filteredLiveNow = liveNowClasses.filter((c: any) => {
    if (!enrolledCourseTitle) return false;
    if (enrolledBatchName && c.batch !== enrolledBatchName) return false;
    return c.course === enrolledCourseTitle;
  });

  return (
    <div className="space-y-6">
      <section className="grid gap-6 xl:grid-cols-[1.35fr_0.75fr]">
        <div className="relative overflow-hidden rounded-[40px] bg-gradient-to-br from-[#c8efff] via-[#eaf8ff] to-white p-6 shadow-sm sm:p-8">
          <div className="absolute -left-12 -top-12 h-52 w-52 rounded-full bg-blue-300/40 blur-3xl" />
          <div className="absolute -bottom-16 right-16 h-60 w-60 rounded-full bg-cyan-300/50 blur-3xl" />

          <div className="absolute bottom-0 right-0 hidden w-[405px] translate-x-2 translate-y-3 md:block">
            <div className="relative">
              <div className="absolute bottom-0 left-16 h-44 w-56 rounded-full bg-slate-900/10 blur-2xl" />
              <div className="absolute bottom-2 left-16 h-28 w-56 rounded-full bg-blue-500/20 blur-3xl" />
              <img
                src={HERO_IMAGE_URL}
                alt="Student learning illustration"
                className="relative z-10 h-[360px] w-full object-contain drop-shadow-[0_34px_28px_rgba(15,23,42,0.24)]"
                onError={(e) => {
                  const target = e.currentTarget;
                  target.style.display = "none";
                }}
              />
            </div>
          </div>

          <div className="relative max-w-[720px] pr-0 md:pr-[260px] xl:pr-[320px]">
            <p className="text-sm font-black uppercase tracking-wide text-blue-700">
              Welcome Back
            </p>

            <h1 className="mt-3 max-w-2xl text-3xl font-black tracking-[-0.05em] text-slate-950 sm:text-5xl">
              Grow your confidence with smart learning.
            </h1>

            <p className="mt-4 max-w-xl text-sm font-semibold leading-6 text-slate-600">
              Aapke dashboard me demo subjects, live classes, tests, course
              access aur progress sab ek hi jagah manage hoga.
            </p>

            <div className="mt-6 grid max-w-xl gap-3 sm:grid-cols-3">
              <MiniHeroCard label="Live" value={String(filteredLiveNow.length)} />
              <MiniHeroCard
                label="Demo Subjects"
                value={String(demoSubjects.length)}
              />
              <MiniHeroCard label="Course" value={activeCourse ? "1" : "0"} />
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <button
                onClick={() => setActiveSection("live")}
                className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
              >
                View Classes
              </button>
              <button
                onClick={() => setActiveSection("courses")}
                className="rounded-2xl bg-white px-5 py-3 text-sm font-black text-slate-800 hover:bg-slate-50"
              >
                Explore Courses
              </button>
            </div>
          </div>
        </div>

        <div className="rounded-[38px] bg-white p-6 shadow-sm">
          <p className="text-sm font-black uppercase tracking-wide text-blue-600">
            Student Info
          </p>

          <div className="mt-5 space-y-4">
            <InfoRow label="Name" value={student.fullName || "Student"} />
            <InfoRow label="Class" value={student.studentClass || "--"} />
            <InfoRow label="Course" value={activeCourse?.title || student.course || "Demo access"} />
            <InfoRow
              label="Access"
              value={
                accessType === "ACTIVE"
                  ? "Premium Active"
                  : accessType === "PENDING"
                  ? "Pending Approval"
                  : "Demo Access"
              }
            />
          </div>

          <button
            onClick={() => (window.location.href = "/student-dashboard/profile")}
            className="mt-6 w-full rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white hover:bg-blue-600"
          >
            View Full Profile
          </button>
        </div>
      </section>

      <section className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
        <FeatureCard
          title="Live Now"
          value={accessType === "ACTIVE" ? String(filteredLiveNow.length) : "0"}
          desc={accessType === "ACTIVE" ? "Join available" : "Demo access"}
        />
        <FeatureCard
          title="Upcoming"
          value={accessType === "ACTIVE" ? String(upcomingClasses.length) : "0"}
          desc="Scheduled classes"
        />
        <FeatureCard
          title="Recordings"
          value={accessType === "ACTIVE" ? String(completedClasses.length) : "0"}
          desc="Completed classes"
        />
        <FeatureCard
          title="Demo Subjects"
          value={String(demoSubjects.length)}
          desc="Free preview"
        />
      </section>

      <section className="grid gap-6 xl:grid-cols-[1fr_360px]">
        <div className="rounded-[34px] bg-white p-6 shadow-sm">
          <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
            <div>
              <p className="text-sm font-black uppercase tracking-wide text-blue-600">
                Demo Learning
              </p>
              <h2 className="mt-2 text-2xl font-black text-slate-950">
                Subject Preview Classes
              </h2>
            </div>

            <button
              onClick={() => setActiveSection("live")}
              className="w-fit rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
            >
              View All
            </button>
          </div>

          <div className="mt-6 grid gap-4 xl:grid-cols-2">
            {accessType === "ACTIVE" &&
              [...filteredLiveNow, ...upcomingClasses].slice(0, 3).map((item) => (
                <LiveClassCard
                  key={item.id}
                  item={item}
                  joinLiveClass={joinLiveClass}
                />
              ))}

            {accessType !== "ACTIVE" &&
              demoSubjects.slice(0, 4).map((item) => (
                <DemoSubjectCard key={item.title} item={item} />
              ))}

            {accessType === "ACTIVE" &&
              filteredLiveNow.length === 0 &&
              upcomingClasses.length === 0 && (
                <EmptyState
                  title="No live class available"
                  desc="Abhi aapke batch ke liye live class scheduled nahi hai."
                />
              )}
          </div>
        </div>

        <div className="rounded-[34px] bg-white p-6 shadow-sm">
          <p className="text-sm font-black uppercase tracking-wide text-blue-600">
            My Course
          </p>

          {accessType === "ACTIVE" ? (
            <div className="mt-5 rounded-[26px] bg-[#f6f9ff] p-5">
              <h3 className="text-xl font-black text-slate-950">
                {activeCourse?.title || student.course}
              </h3>
              <p className="mt-2 text-sm font-bold text-slate-500">
                {activeCourse?.category || student.studentClass} • Mentor:{" "}
                {activeCourse?.teacher || "Assigned Teacher"}
              </p>

              <div className="mt-5">
                <div className="mb-2 flex justify-between text-xs font-black text-slate-500">
                  <span>Progress</span>
                  <span>{activeCourse?.progress || 0}%</span>
                </div>

                <div className="h-3 overflow-hidden rounded-full bg-white">
                  <div
                    className="h-full rounded-full bg-blue-600"
                    style={{ width: `${activeCourse?.progress || 0}%` }}
                  />
                </div>
              </div>
            </div>
          ) : (
            <UpgradeBox
              title="Demo access active"
              desc="Full course subscribe karne ke baad live classes, notes, recordings aur tests unlock honge."
              buttonText="Explore Courses"
              onClick={() => setActiveSection("courses")}
            />
          )}
        </div>
      </section>
    </div>
  );
};

const CoursesSection = ({
  courses,
  student,
  accessType,
  recommendedCourses,
  demoSubjects,
  myEnrollments,
}: {
  courses: Course[];
  student: StudentInfo;
  accessType: AccessType;
  recommendedCourses: Course[];
  demoSubjects: DemoSubject[];
  myEnrollments: any[];
}) => {
  return (
    <div className="space-y-6">
      <PageHeader
        label="Courses"
        title="Your courses and subscriptions"
        desc="Aap apne class/goal ke according demo classes aur course subscription yaha dekh sakte ho."
      />

      {accessType !== "ACTIVE" && (
        <div className="rounded-[34px] border border-blue-200 bg-blue-50 p-6">
          <h2 className="text-2xl font-black text-blue-900">
            Demo subjects available
          </h2>
          <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-blue-700">
            Abhi aap demo access par ho. Neeche subject-wise demo classes dekh
            sakte ho. Full course ke liye subscription required hoga.
          </p>

          <div className="mt-5 grid gap-4 xl:grid-cols-3">
            {demoSubjects.map((item) => (
              <DemoSubjectCard key={item.title} item={item} />
            ))}
          </div>
        </div>
      )}

      {accessType === "ACTIVE" && student.course && (
        <div className="rounded-[34px] bg-white p-6 shadow-sm">
          <p className="text-sm font-black uppercase tracking-wide text-emerald-600">
            Active Subscription
          </p>
          <h2 className="mt-2 text-2xl font-black text-slate-950">
            {student.course}
          </h2>
          <p className="mt-2 text-sm font-semibold text-slate-500">
            Is course ke live classes, recordings, notes aur tests unlock hain.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 gap-5 xl:grid-cols-2">
        {courses.map((course: Course) => {
          
          const isEnrolled = myEnrollments.some((e: any) => {
             const status = String(e.status || e.paymentStatus || "").toUpperCase().trim();
             const isActive = e.courseAccessEnabled || e.accessEnabled || ["APPROVED", "PAID", "SUCCESS"].includes(status);
             return isActive && (e.courseId === course.id || e.course_id === course.id);
          });

          return (
            <article 
              key={course.id} 
              className={`group rounded-[26px] border transition-all hover:-translate-y-1 hover:shadow-[0_18px_50px_rgba(15,23,42,0.10)] ${isEnrolled ? "border-emerald-300 bg-emerald-50/20 shadow-md" : "border-slate-200 bg-white shadow-sm hover:border-blue-200"}`}
            >
              <div className="mb-5 flex items-start justify-between gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-xl">🎓</div>
                {isEnrolled ? (
                   <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-black text-emerald-700">
                     Already Enrolled
                   </span>
                ) : course.tag ? (
                  <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-black text-orange-600">
                    {course.tag}
                  </span>
                ) : null}
              </div>
              
              <p className="text-xs font-black uppercase tracking-[0.14em] text-blue-600">{course.category}</p>
              <h3 className="mt-2 text-2xl font-black leading-tight tracking-[-0.03em] text-slate-950">{course.title}</h3>
              
              {course.seoTitle && (
                <p className="mt-2 text-sm font-bold text-blue-700">{course.seoTitle}</p>
              )}
              
              <p className="mt-3 min-h-[72px] text-sm leading-6 text-slate-600">
                {course.short_desc || course.desc || "Learn top skills with this complete structured program designed for your success."}
              </p>
              
              <div className="mt-5 grid grid-cols-3 gap-3">
                <div className="rounded-2xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Duration</p>
                  <p className="mt-1 text-sm font-black text-slate-950">{course.duration || "N/A"}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Level</p>
                  <p className="mt-1 text-sm font-black text-slate-950">{course.level || "Beginner"}</p>
                </div>
                <div className="rounded-2xl bg-slate-50 p-3">
                  <p className="text-xs text-slate-500">Fee</p>
                  <p className="mt-1 text-sm font-black text-slate-950">₹{course.price}</p>
                </div>
              </div>
              
              {course.features && Array.isArray(course.features) && course.features.length > 0 && (
                <div className="mt-5 space-y-2">
                  {course.features.slice(0, 3).map((feature: string, idx: number) => (
                    <div key={idx} className="flex items-center gap-2">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-green-50 text-xs text-green-600">✓</span>
                      <p className="text-sm font-medium text-slate-700">{feature}</p>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-6 flex gap-3">
                {isEnrolled ? (
                  <button 
                    onClick={() => (window.location.href = `/student-dashboard/course/${course.id}`)} 
                    className="flex-1 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-black text-white transition-all hover:bg-emerald-700"
                  >
                    Open Course Dashboard
                  </button>
                ) : (
                  <button 
                    onClick={() => (window.location.href = `/course-purchase/${course.id}`)} 
                    className="flex-1 rounded-xl bg-[#101827] px-5 py-3 text-sm font-black text-white transition-all hover:bg-slate-800"
                  >
                    Enroll Now
                  </button>
                )}
                
                <a 
                  href={`/courses/${course.slug || course.id}`} 
                  className="rounded-xl border border-slate-300 px-5 py-3 text-sm font-black text-slate-950 transition-all hover:bg-slate-50"
                >
                  Details
                </a>
              </div>
            </article>
          );
        })}

        {courses.length === 0 && (
          <EmptyState
            title="No courses found"
            desc="Admin ne abhi courses add nahi kiye."
          />
        )}
      </div>
    </div>
  );
};

const LiveClassesSection = ({
  accessType,
  liveNowClasses,
  upcomingClasses,
  completedClasses,
  demoSubjects,
  joinLiveClass,
  setActiveSection,
  activeEnrollment,
  activeCourse,
}: {
  accessType: AccessType;
  liveNowClasses: LiveClass[];
  upcomingClasses: LiveClass[];
  completedClasses: LiveClass[];
  demoSubjects: DemoSubject[];
  joinLiveClass: (value: LiveClass) => void;
  setActiveSection: (value: ActiveSection) => void;
  activeEnrollment: any;
  activeCourse: Course | null;
}) => {
  
  const enrolledBatchName = activeEnrollment?.batchName || activeEnrollment?.batch_name;
  const enrolledCourseTitle = activeCourse?.title;

  const filterByEnrollment = (classesList: LiveClass[]) => {
    return classesList.filter((c: any) => {
       if (!enrolledCourseTitle) return false;
       if (enrolledBatchName && c.batch !== enrolledBatchName) return false;
       return c.course === enrolledCourseTitle;
    });
  };

  const filteredLiveNow = filterByEnrollment(liveNowClasses);
  const filteredUpcoming = filterByEnrollment(upcomingClasses);
  const filteredCompleted = filterByEnrollment(completedClasses);

  return (
    <div className="space-y-6">
      <PageHeader
        label="Live Classes"
        title="Classes, demo videos and recordings"
        desc="Active subscription par aapke batch ki real live classes dikhenge. Free account par demo classes available rahengi."
      />

      {accessType !== "ACTIVE" && (
        <div className="rounded-[34px] bg-white p-6 shadow-sm">
          <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-center">
            <div>
              <h2 className="text-2xl font-black text-slate-950">
                Demo live class preview
              </h2>
              <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-500">
                Abhi aap demo lectures dekh sakte ho. Full live classroom,
                attendance, notes aur recording ke liye course subscribe karein.
              </p>
            </div>

            <button
              onClick={() => setActiveSection("courses")}
              className="rounded-2xl bg-blue-600 px-5 py-3 text-sm font-black text-white hover:bg-blue-700"
            >
              Subscribe Course
            </button>
          </div>

          <div className="mt-6 grid gap-4 xl:grid-cols-3">
            {demoSubjects.map((item) => (
              <DemoSubjectCard key={item.title} item={item} />
            ))}
          </div>
        </div>
      )}

      {accessType === "ACTIVE" && (
        <>
          <div className="grid gap-5 xl:grid-cols-2">
            {[...filteredLiveNow, ...filteredUpcoming].map((item) => (
              <LiveClassCard
                key={item.id}
                item={item}
                joinLiveClass={joinLiveClass}
              />
            ))}
          </div>

          {filteredLiveNow.length === 0 && filteredUpcoming.length === 0 && (
            <EmptyState
              title="No live or upcoming class"
              desc="Aapke batch ke liye abhi koi class scheduled nahi hai."
            />
          )}

          <div className="rounded-[34px] bg-white p-6 shadow-sm">
            <h2 className="text-2xl font-black text-slate-950">
              Completed Recordings
            </h2>

            <div className="mt-5 grid gap-4 xl:grid-cols-2">
              {filteredCompleted.map((item) => (
                <div key={item.id} className="rounded-[24px] bg-[#f6f9ff] p-5">
                  <StatusBadge status={item.status} />
                  <h3 className="mt-3 text-lg font-black text-slate-950">
                    {item.title}
                  </h3>
                  <p className="mt-1 text-sm font-bold text-slate-500">
                    {item.subject} • {item.teacher}
                  </p>

                  {item.recordingUrl ? (
                    <a
                      href={item.recordingUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-4 inline-flex rounded-2xl bg-emerald-50 px-5 py-3 text-sm font-black text-emerald-700 hover:bg-emerald-100"
                    >
                      Watch Recording
                    </a>
                  ) : (
                    <p className="mt-4 text-sm font-bold text-slate-400">
                      Recording not uploaded yet.
                    </p>
                  )}
                </div>
              ))}

              {filteredCompleted.length === 0 && (
                <EmptyState
                  title="No recordings yet"
                  desc="Completed classes ke baad recordings yaha dikhenge."
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const TestsSection = ({
  accessType,
  demoSubjects,
  setActiveSection,
  tests,
  activeEnrollment,
  activeCourse
}: {
  accessType: AccessType;
  demoSubjects: DemoSubject[];
  setActiveSection: (value: ActiveSection) => void;
  tests: any[];
  activeEnrollment: any;
  activeCourse: Course | null;
}) => {

  const enrolledBatchId = String(activeEnrollment?.batchId || activeEnrollment?.batch_id || "");
  const enrolledBatchName = String(activeEnrollment?.batchName || activeEnrollment?.batch_name || "").toLowerCase().trim();
  
  // 🚀 PERFECTION FILTER: General Tests + Student's Own Batch Tests Both Visible
  const filteredTests = tests.filter((t: any) => {
    if (!activeEnrollment) return false; 
    
    const testBatchId = String(t.batchId || t.batch_id || "");
    const testBatchName = String(t.batchName || t.batch_name || "").toLowerCase().trim();

    // Condition 1: Agar test General Batch ka hai (sabke liye)
    if (!testBatchId || testBatchId === "null" || testBatchName === "general batch" || testBatchName === "null") {
      return true;
    }

    // Condition 2: Agar test student ke exact enrolled batch ID se match karta hai
    if (enrolledBatchId && testBatchId === enrolledBatchId) {
      return true;
    }

    // Baaki doosre batches ke tests hide rahenge
    return false;
  });

  return (
    <div className="space-y-6">
      <PageHeader
        label="Test Series"
        title="Practice tests and mock exams"
        desc="Aapke enrolled course aur batch ke hisaab se tests yahan dikhenge."
      />

      <div className="grid gap-5 xl:grid-cols-3">
        <FeatureCard title="Total Tests" value={String(filteredTests?.length || 0)} desc="Available" />
        <FeatureCard title="Attempted" value="0" desc="Completed tests" />
        <FeatureCard title="Avg Score" value="--" desc="Yet to attempt" />
      </div>

      {accessType !== "ACTIVE" && (
        <div className="rounded-[34px] bg-white p-6 shadow-sm">
          <h2 className="text-2xl font-black text-slate-950">Subject-wise demo practice</h2>
          <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
            Demo ke taur par basic subject practice aur YouTube preview dekh sakte ho.
          </p>
          <div className="mt-6 grid gap-4 xl:grid-cols-3">
            {demoSubjects.map((item: any) => (
              <DemoSubjectCard key={item.title} item={item} />
            ))}
          </div>
          <button
            onClick={() => setActiveSection("courses")}
            className="mt-6 rounded-2xl bg-blue-600 px-6 py-3 text-sm font-black text-white hover:bg-blue-700"
          >
            Unlock Full Test Series
          </button>
        </div>
      )}

      {accessType === "ACTIVE" && (
        <div className="rounded-[34px] bg-white p-6 shadow-sm">
          <h2 className="text-2xl font-black text-slate-950 mb-6">Active Test Series</h2>
          
          {filteredTests && filteredTests.length > 0 ? (
             <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
               {filteredTests.map((test) => {
                 
                 // --- TEST STATUS LOGIC ---
                 const now = new Date();
                 const sTime = test.startTime || test.start_time;
                 const eTime = test.endTime || test.end_time;
                 const startDate = sTime ? new Date(sTime) : null;
                 const endDate = eTime ? new Date(eTime) : null;

                 let testStatus = "ACTIVE"; 
                 
                 if (test.is_attempted || test.isCompleted) {
                   testStatus = "COMPLETED";
                 } else if (startDate && now < startDate) {
                   testStatus = "UPCOMING";
                 } else if (endDate && now > endDate) {
                   testStatus = "EXPIRED";
                 }

                 return (
                   <div key={test.id} className="rounded-2xl border border-slate-200 bg-[#f8fbff] p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-md">
                      <div className="flex justify-between items-start mb-4">
                        <span className="bg-blue-100 text-blue-800 text-[10px] font-black uppercase px-2 py-1 rounded-full">
                          {test.batchName || "General Batch"}
                        </span>
                        <span className="text-xs font-bold text-slate-400">ID: {test.id}</span>
                      </div>
                      
                      <h3 className="text-lg font-black text-slate-900 mb-2">{test.title}</h3>
                      
                      <div className="flex flex-col gap-1 text-xs font-bold text-slate-600 mb-6">
                        <div className="flex gap-4">
                          <span>Marks: {test.total_marks || test.totalMarks}</span>
                          <span>Time: {test.duration_minutes || test.duration} mins</span>
                        </div>
                        {startDate && endDate && (
                           <div className="mt-2 text-[10px] text-slate-500 bg-slate-100 p-2 rounded-lg border border-slate-200">
                             <div className="text-emerald-700">Starts: {startDate.toLocaleString('en-IN')}</div>
                             <div className="text-rose-700 mt-1">Ends: {endDate.toLocaleString('en-IN')}</div>
                           </div>
                        )}
                      </div>

                      {/* DYNAMIC BUTTONS BASED ON DATE/TIME STATUS */}
                      {testStatus === "ACTIVE" && (
                        <button 
                          onClick={() => window.location.href=`/student-dashboard/quiz/${test.id}`}
                          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black text-sm py-3 rounded-xl transition shadow-md shadow-blue-600/20"
                        >
                          Start Test
                        </button>
                      )}

                      {testStatus === "UPCOMING" && (
                        <button disabled className="w-full bg-slate-200 text-slate-500 font-black text-sm py-3 rounded-xl cursor-not-allowed">
                          Starts Soon ⏳
                        </button>
                      )}

                      {testStatus === "EXPIRED" && (
                        <button disabled className="w-full bg-rose-50 text-rose-500 border border-rose-200 font-black text-sm py-3 rounded-xl cursor-not-allowed">
                          Test Expired 🚫
                        </button>
                      )}

                      {testStatus === "COMPLETED" && (
                        <button onClick={() => alert("Result dashboard par redirect karein...")} className="w-full bg-emerald-50 text-emerald-700 border border-emerald-200 font-black text-sm py-3 rounded-xl hover:bg-emerald-100 transition">
                          View Result ✓
                        </button>
                      )}
                   </div>
                 );
               })}
             </div>
          ) : (
            <div className="text-center py-10 border border-dashed border-slate-200 rounded-2xl bg-slate-50">
              <span className="text-4xl mb-3 block opacity-50">📝</span>
              <p className="text-sm font-bold text-slate-500">
                Aapke batch ke liye abhi koi test assign nahi hua hai.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const NotificationsSection = ({
  notifications,
}: {
  notifications: NotificationItem[];
}) => {
  return (
    <div className="space-y-6">
      <PageHeader
        label="Notifications"
        title="Updates and reminders"
        desc="Live class reminders, tests aur subscription alerts yaha show honge."
      />

      <div className="rounded-[34px] bg-white p-6 shadow-sm">
        <div className="space-y-4">
          {notifications.map((item) => (
            <div
              key={item.id}
              className="flex gap-4 rounded-[24px] bg-[#f6f9ff] p-5"
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-xl shadow-sm">
                {item.type === "live"
                  ? "🎥"
                  : item.type === "test"
                  ? "🧪"
                  : item.type === "payment"
                  ? "💳"
                  : "📘"}
              </div>

              <div className="min-w-0 flex-1">
                <h3 className="text-base font-black text-slate-950">
                  {item.title}
                </h3>
                <p className="mt-1 text-sm font-semibold leading-6 text-slate-500">
                  {item.desc}
                </p>
                <p className="mt-2 text-xs font-black text-blue-600">
                  {item.time}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SettingsSection = ({ logoutUser }: { logoutUser: () => void }) => {
  return (
    <div className="space-y-6">
      <PageHeader
        label="Settings"
        title="Account settings"
        desc="Account preferences aur logout yaha se manage karein."
      />

      <div className="grid gap-5 xl:grid-cols-2">
        <div className="rounded-[34px] bg-white p-6 shadow-sm">
          <h2 className="text-2xl font-black text-slate-950">
            Notification Preferences
          </h2>

          <div className="mt-5 space-y-3">
            {[
              "Live class reminders",
              "Test and quiz alerts",
              "Course and payment updates",
              "Study material updates",
            ].map((item) => (
              <label
                key={item}
                className="flex items-center justify-between rounded-2xl bg-[#f6f9ff] p-4"
              >
                <span className="text-sm font-bold text-slate-700">{item}</span>
                <input
                  type="checkbox"
                  defaultChecked
                  className="h-5 w-5 accent-blue-600"
                />
              </label>
            ))}
          </div>
        </div>

        <div className="rounded-[34px] border border-red-100 bg-red-50 p-6">
          <h2 className="text-2xl font-black text-red-600">Logout Account</h2>
          <p className="mt-2 text-sm font-semibold leading-6 text-red-500">
            Ye browser se aapka login token remove karega.
          </p>

          <button
            onClick={logoutUser}
            className="mt-6 rounded-2xl bg-red-600 px-6 py-3 text-sm font-black text-white hover:bg-red-700"
          >
            Logout Now
          </button>
        </div>
      </div>
    </div>
  );
};

const DemoSubjectCard = ({ item }: { item: DemoSubject }) => {
  return (
    <div className="rounded-[28px] border border-blue-100 bg-white p-5 shadow-sm transition hover:-translate-y-1 hover:shadow-xl">
      <div className="flex items-start justify-between gap-3">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-lg font-black text-blue-700">
          {item.icon}
        </div>

        <span className="rounded-full bg-cyan-50 px-3 py-1 text-[11px] font-black text-cyan-700">
          {item.tag}
        </span>
      </div>

      <h3 className="mt-4 text-lg font-black text-slate-950">{item.title}</h3>
      <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
        {item.desc}
      </p>

      <a
        href={item.youtubeUrl}
        target="_blank"
        rel="noreferrer"
        className="mt-5 inline-flex rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white hover:bg-blue-600"
      >
        Watch Demo
      </a>
    </div>
  );
};

const UpgradeBox = ({
  title,
  desc,
  buttonText,
  onClick,
}: {
  title: string;
  desc: string;
  buttonText: string;
  onClick: () => void;
}) => {
  return (
    <div className="mt-5 rounded-[28px] border border-blue-100 bg-gradient-to-br from-blue-50 to-white p-6 text-center">
      <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-white text-2xl shadow-sm">
        ✦
      </div>
      <h3 className="mt-4 text-xl font-black text-slate-950">{title}</h3>
      <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
        {desc}
      </p>
      <button
        onClick={onClick}
        className="mt-5 rounded-2xl bg-blue-600 px-6 py-3 text-sm font-black text-white hover:bg-blue-700"
      >
        {buttonText}
      </button>
    </div>
  );
};

const MiniHeroCard = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="rounded-[22px] border border-white/70 bg-white/70 p-4 shadow-sm backdrop-blur">
      <p className="text-xs font-black uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className="mt-1 text-2xl font-black text-slate-950">{value}</p>
    </div>
  );
};

const LiveClassCard = ({
  item,
  joinLiveClass,
}: {
  item: LiveClass;
  joinLiveClass: (value: LiveClass) => void;
}) => {
  const [isTimeReached, setIsTimeReached] = useState(false);

  useEffect(() => {
    if (item.status === "Completed" || item.status === "Cancelled") return;
    
    const checkTime = () => {
      const classDateTime = new Date(`${item.date}T${item.startTime}`);
      const now = new Date();
      if (item.status === "Live" || now >= classDateTime) {
        setIsTimeReached(true);
      } else {
        setIsTimeReached(false);
      }
    };
    
    checkTime();
    const interval = setInterval(checkTime, 60000); 
    return () => clearInterval(interval);
  }, [item.date, item.startTime, item.status]);

  return (
    <div className="rounded-[30px] bg-white p-6 shadow-sm border border-slate-100">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <span className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${isTimeReached ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
          {isTimeReached ? 'Live Now' : 'Upcoming'}
        </span>
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-600">{item.platform}</span>
      </div>

      <h3 className="mt-4 text-xl font-black text-slate-950">{item.title}</h3>

      <p className="mt-2 text-sm font-bold text-slate-500">
        {item.subject} • {item.batch}
      </p>

      <div className="mt-5 grid gap-3 sm:grid-cols-2">
        <InfoBox label="Teacher" value={item.teacher} />
        <InfoBox label="Date" value={item.date} />
        <InfoBox label="Time" value={String(item.startTime).slice(0, 5)} />
        <InfoBox label="Duration" value={`${item.duration} min`} />
      </div>

      <div className="mt-5">
        {item.status === "Completed" ? (
           item.recordingUrl ? (
             <a
               href={item.recordingUrl}
               target="_blank"
               rel="noreferrer"
               className="flex justify-center rounded-2xl bg-emerald-50 px-5 py-3.5 text-sm font-black text-emerald-700"
             >
               Watch Recording
             </a>
           ) : (
             <button className="w-full rounded-2xl bg-slate-100 px-5 py-3.5 text-sm font-black text-slate-500 cursor-not-allowed">
               Completed
             </button>
           )
        ) : isTimeReached ? (
          <button
            onClick={() => joinLiveClass(item)}
            className="w-full rounded-2xl bg-red-600 px-5 py-3.5 text-sm font-black text-white hover:bg-red-700 shadow-md shadow-red-200 transition"
          >
            Join Live Class ▶
          </button>
        ) : (
          <button disabled className="w-full rounded-2xl bg-slate-100 px-5 py-3.5 text-sm font-black text-slate-500 cursor-not-allowed">
            Starts strictly at {String(item.startTime).slice(0, 5)}
          </button>
        )}
      </div>
    </div>
  );
};

const PageHeader = ({
  label,
  title,
  desc,
}: {
  label: string;
  title: string;
  desc: string;
}) => {
  return (
    <div className="rounded-[34px] bg-white p-6 shadow-sm">
      <p className="text-sm font-black uppercase tracking-wide text-blue-600">
        {label}
      </p>
      <h1 className="mt-2 text-3xl font-black tracking-[-0.04em] text-slate-950">
        {title}
      </h1>
      <p className="mt-2 max-w-3xl text-sm font-semibold leading-6 text-slate-500">
        {desc}
      </p>
    </div>
  );
};

const FeatureCard = ({
  title,
  value,
  desc,
}: {
  title: string;
  value: string;
  desc: string;
}) => {
  return (
    <div className="rounded-[30px] bg-white p-6 shadow-sm">
      <p className="text-sm font-bold text-slate-500">{title}</p>
      <h2 className="mt-2 text-4xl font-black text-slate-950">{value}</h2>
      <p className="mt-2 text-xs font-black text-emerald-600">{desc}</p>
    </div>
  );
};

const InfoRow = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="flex items-center justify-between gap-4 rounded-2xl bg-[#f6f9ff] px-4 py-3">
      <span className="text-sm font-bold text-slate-500">{label}</span>
      <span className="text-right text-sm font-black text-slate-950">
        {value}
      </span>
    </div>
  );
};

const InfoBox = ({ label, value }: { label: string; value: string }) => {
  return (
    <div className="rounded-2xl bg-[#f6f9ff] p-4">
      <p className="text-xs font-bold text-slate-400">{label}</p>
      <p className="mt-1 break-words text-sm font-black text-slate-900">
        {value || "--"}
      </p>
    </div>
  );
};

const StatusBadge = ({ status }: { status: LiveStatus }) => {
  return (
    <span
      className={`inline-flex rounded-full px-3 py-1 text-xs font-black ${
        status === "Live"
          ? "bg-red-50 text-red-700"
          : status === "Scheduled"
          ? "bg-blue-50 text-blue-700"
          : status === "Completed"
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-600"
      }`}
    >
      {status}
    </span>
  );
};

const EmptyState = ({ title, desc }: { title: string; desc: string }) => {
  return (
    <div className="rounded-[26px] bg-white p-8 text-center shadow-sm">
      <h3 className="text-xl font-black text-slate-950">{title}</h3>
      <p className="mt-2 text-sm font-bold text-slate-500">{desc}</p>
    </div>
  );
};

export default Dashboard;